// Export tables to a .sql file that runs in pgAdmin against a local database.
//
// Two independent choices per table: whether it is included at all (schema is
// always emitted for an included table), and whether its rows come too. That
// split exists so a large table can be brought over structurally without
// dragging tens of megabytes of INSERTs with it.

import { useState, useEffect, useMemo } from 'react';
import { exportApi } from './exportApi.js';
import { triggerDownload } from '../../shared/http/download.js';
import { T } from '../../shared/styles/tokens.js';

const key = (t) => `${t.schema}.${t.name}`;

function fmtRows(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function fmtBytes(n) {
  if (!n) return '—';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

export default function ExportPage() {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [withData, setWithData] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    exportApi.listTables()
      .then(d => setTables(d.tables || []))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (set, setter) => (k) => setter(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const toggleSelected = toggle(selected, setSelected);
  const toggleData = toggle(withData, setWithData);

  // Rough guide only: INSERT-as-text runs well above the on-disk size, and only
  // tables carrying data contribute. Labelled approximate in the UI.
  const estimate = useMemo(() => {
    let bytes = 0, rows = 0, dataTables = 0;
    for (const t of tables) {
      const k = key(t);
      if (!selected.has(k) || !withData.has(k)) continue;
      dataTables += 1;
      rows += t.approx_rows;
      bytes += t.total_bytes * 3;
    }
    return { bytes, rows, dataTables };
  }, [tables, selected, withData]);

  async function run() {
    if (!selected.size || running) return;
    setRunning(true); setErr(null); setResult(null);
    try {
      const payload = tables
        .filter(t => selected.has(key(t)))
        .map(t => ({ schema: t.schema, name: t.name, include_data: withData.has(key(t)) }));
      const { blob, headers } = await exportApi.run(payload);
      const stamp = new Date().toISOString().slice(0, 10);
      triggerDownload(blob, `orbit-export-${stamp}.sql`);

      let summary = null;
      try { summary = JSON.parse(headers.get('X-Export-Summary') || 'null'); } catch { /* header is optional */ }
      setResult({ size: blob.size, summary });
    } catch (e) {
      setErr(e.message);
    } finally {
      setRunning(false);
    }
  }

  if (loading) return <div className="o-state">Loading tables…</div>;

  return (
    <div className="o-page">
      <div className="o-head-row">
        <div>
          <p className="o-eyebrow">Export</p>
          <h1 className="o-title">
            {selected.size} table{selected.size !== 1 ? 's' : ''}
            {estimate.dataTables > 0 && (
              <span style={{ color: T.text3, fontWeight: 400 }}>
                {' · '}{estimate.dataTables} with data · ~{fmtBytes(estimate.bytes)}
              </span>
            )}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="o-btn o-btn-sm"
            onClick={() => setSelected(new Set(tables.map(key)))}>Select all</button>
          <button className="o-btn o-btn-sm"
            onClick={() => { setSelected(new Set()); setWithData(new Set()); }}>Clear</button>
          <button className="o-btn o-btn-sm"
            onClick={() => setWithData(new Set(selected))}>Data: all</button>
          <button className="o-btn o-btn-sm"
            onClick={() => setWithData(new Set())}>Data: none</button>
          <button className="o-btn o-btn-primary" onClick={run} disabled={!selected.size || running}>
            {running ? 'Generating…' : 'Download .sql'}
          </button>
        </div>
      </div>

      <p className="o-note">
        Runs in pgAdmin's Query Tool and is safe to re-run — tables exported with data are
        emptied and refilled, so local ends up mirroring this database. Foreign-key checks
        are suspended for the session, which needs superuser on the target. Indexes,
        constraints and triggers are not exported; migrations own those.
      </p>

      {err && <div className="o-state o-error" style={{ textAlign: 'left', padding: `${T.s3}px 0` }}>{err}</div>}

      {result && (
        <div className="o-panel" style={{ padding: `${T.s3}px ${T.s4}px`, marginBottom: T.s4 }}>
          <span className="o-badge o-ok">Downloaded</span>{' '}
          <span style={{ fontSize: T.tSmall, color: T.text2 }}>
            {fmtBytes(result.size)}
            {result.summary && ` · ${result.summary.filter(s => s.include_data)
              .reduce((n, s) => n + s.rows, 0).toLocaleString()} rows`}
          </span>
        </div>
      )}

      <div className="o-panel" style={{ overflowX: 'auto' }}>
        <table className="o-tbl">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Schema</th>
              <th>Table</th>
              <th>Rows (approx)</th>
              <th>Size</th>
              <th style={{ width: 90 }}>Data</th>
            </tr>
          </thead>
          <tbody>
            {tables.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: T.text3 }}>No tables found.</td></tr>
            )}
            {tables.map(t => {
              const k = key(t);
              const on = selected.has(k);
              return (
                <tr key={k} style={{ opacity: on ? 1 : 0.55 }}>
                  <td>
                    <input type="checkbox" checked={on} onChange={() => toggleSelected(k)}
                      aria-label={`Include ${k}`}
                      style={{ accentColor: T.ink, width: 15, height: 15, cursor: 'pointer' }} />
                  </td>
                  <td style={{ color: T.text2 }}>{t.schema}</td>
                  <td className="o-mono o-strong">{t.name}</td>
                  <td className="o-num">{fmtRows(t.approx_rows)}</td>
                  <td style={{ color: T.text3 }}>{fmtBytes(t.total_bytes)}</td>
                  <td>
                    <input type="checkbox" checked={on && withData.has(k)} disabled={!on}
                      onChange={() => toggleData(k)}
                      aria-label={`Include data for ${k}`}
                      style={{ accentColor: T.ink, width: 15, height: 15,
                               cursor: on ? 'pointer' : 'default' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
